import { Link } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { BenchmarkSummaryItem } from "@/api/intelligence";
import { formatCount, formatInr, formatPercentChange } from "@/lib/intelligenceFormat";
import { formatBenchmarkSource } from "@/lib/benchmarkSourceLabel";
import { cn } from "@/lib/utils";

type Props = {
	items: BenchmarkSummaryItem[];
	limitations?: string[];
	basePath?: string;
	enabled?: boolean;
};

function formatValue(metricKey: string, value: number | null): string {
	if (value === null) return "—";
	if (
		metricKey.includes("revenue") ||
		metricKey.includes("billing") ||
		metricKey.includes("collections") ||
		metricKey.includes("outstanding")
	) {
		return formatInr(value, 0);
	}
	return formatCount(value);
}

function directionLabel(direction: BenchmarkSummaryItem["direction"]): string {
	if (direction === "ABOVE") return "Above benchmark";
	if (direction === "BELOW") return "Below benchmark";
	if (direction === "AT") return "At benchmark";
	return "—";
}

function directionClass(direction: BenchmarkSummaryItem["direction"]): string {
	if (direction === "ABOVE") return "text-emerald-700";
	if (direction === "BELOW") return "text-rose-700";
	return "text-muted-foreground";
}

export function BenchmarkSummarySection({
	items,
	limitations = [],
	basePath = "/hospital-admin",
	enabled = true,
}: Props) {
	const benchmarksPath = `${basePath}/intelligence/benchmarks`;

	if (!enabled || items.length === 0) {
		return (
			<section className="space-y-3">
				<div className="flex items-center justify-between gap-2">
					<div>
						<h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
							<BarChart3 className="h-5 w-5 text-indigo-600" />
							Benchmarks
						</h2>
						<p className="text-sm text-muted-foreground">
							Select a single hospital to compare performance against chain or group averages.
						</p>
					</div>
					<Link to={benchmarksPath} className="text-xs font-medium text-indigo-600 hover:underline shrink-0">
						View all →
					</Link>
				</div>
				{limitations.length > 0 && (
					<Card className="border-slate-200/80">
						<CardContent className="pt-4 text-sm text-muted-foreground">{limitations[0]}</CardContent>
					</Card>
				)}
			</section>
		);
	}

	return (
		<section className="space-y-3">
			<div className="flex items-center justify-between gap-2">
				<div>
					<h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
						<BarChart3 className="h-5 w-5 text-indigo-600" />
						Benchmarks
					</h2>
					<p className="text-sm text-muted-foreground">
						How this hospital compares to chain / group peers for key metrics.
					</p>
				</div>
				<Link to={benchmarksPath} className="text-xs font-medium text-indigo-600 hover:underline shrink-0">
					View all →
				</Link>
			</div>

			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				{items.map((item) => (
					<Card key={item.metricKey} className="border-slate-200/80 shadow-sm">
						<CardHeader className="pb-2">
							<CardTitle className="text-sm font-medium text-muted-foreground">{item.metricLabel}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							<p className="text-xl font-semibold tabular-nums text-slate-900">
								{formatValue(item.metricKey, item.currentValue)}
							</p>
							<div className="text-xs space-y-1">
								<p className="text-muted-foreground">
									Benchmark: {formatValue(item.metricKey, item.benchmarkValue)}
									{item.sampleSize > 0 ? ` · n=${item.sampleSize}` : ""}
								</p>
								<p className={cn("font-medium tabular-nums", directionClass(item.direction))}>
									{item.percentageVariance !== null
										? formatPercentChange(item.percentageVariance)
										: "—"}{" "}
									· {directionLabel(item.direction)}
								</p>
								<p className="text-muted-foreground truncate" title={formatBenchmarkSource(item.benchmarkSource)}>
									{formatBenchmarkSource(item.benchmarkSource)}
								</p>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{limitations.length > 0 && (
				<p className="text-xs text-muted-foreground">{limitations.join(" ")}</p>
			)}
		</section>
	);
}
